import React from 'react'
import AppDateInput from '../AppDateInput'
import AppDropdown from '../AppDropdown'
import AppEditableTimeField from '../AppEditableTimeField'

export default function ScheduleTimingSection({
  // Date range
  showDate = false,
  date = '',
  onDateChange,
  dateLabel = 'Date',
  showTillDate = false,
  tillDate = '',
  onTillDateChange,
  tillDateLabel = 'Till Date',

  // Day of Week
  showDay = false,
  day = 'Monday',
  onDayChange,
  dayOptions = [],

  // Ignore Duplication
  showIgnoreDuplication = false,
  ignoreDuplication = false,
  onIgnoreDuplicationChange,
  ignoreDuplicationLabel = 'Ignore Duplication',

  // Time
  showTime = true,
  // Single object style
  planTime,
  onPlanTimeChange,
  tillTime,
  onTillTimeChange,
  // Individual fields style
  planHour,
  planMinute,
  planAmPm,
  onPlanHourChange,
  onPlanMinuteChange,
  onPlanAmPmChange,
  tillHour,
  tillMinute,
  tillAmPm,
  onTillHourChange,
  onTillMinuteChange,
  onTillAmPmChange,
}) {
  return (
    <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs">
      {/* ── Header ── */}
      <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 rounded-t-3xl flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
          <span className="material-symbols-outlined text-lg">calendar_month</span>
        </div>
        <div>
          <h2 className="font-display text-base font-bold text-on-surface">
            Schedule &amp; Timing
          </h2>
          <p className="text-xs text-on-surface-variant">
            Configure dates, recurring weekdays, and plan/till time intervals.
          </p>
        </div>
      </div>

      {/* ── Fields ── */}
      <div className="p-6 md:p-8 flex flex-col gap-5 text-xs">
        
        {/* Day of Week & Ignore Duplication (For Timetable) */}
        {showDay && (
          <div className="grid grid-cols-12 gap-4 items-center">
            <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
              Day<span className="text-error font-bold"> *</span>
            </span>
            <div className={`col-span-12 ${showIgnoreDuplication ? 'sm:col-span-5' : 'sm:col-span-9'}`}>
              <AppDropdown
                options={dayOptions}
                value={day}
                onChange={onDayChange}
                triggerClassName="h-10 w-full bg-surface-container-low text-xs font-semibold"
              />
            </div>
            {showIgnoreDuplication && (
              <div className="col-span-12 sm:col-span-4">
                <label className="inline-flex items-center gap-2 rounded-xl bg-surface-container-low px-3.5 py-2 border border-outline-variant/20 cursor-pointer select-none text-xs font-bold text-on-surface transition hover:bg-surface-container-high">
                  <input
                    type="checkbox"
                    checked={ignoreDuplication}
                    onChange={(e) => onIgnoreDuplicationChange?.(e.target.checked)}
                    className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 accent-primary"
                  />
                  <span>{ignoreDuplicationLabel}</span>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Date & Till Date (For Lectures / Syllabus) */}
        {(showDate || showTillDate) && (
          <div className="grid grid-cols-12 gap-4 items-center">
            {showDate && (
              <>
                <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  {dateLabel}<span className="text-error font-bold"> *</span>
                </span>
                <div className={`col-span-12 ${showTillDate ? 'sm:col-span-4' : 'sm:col-span-9'}`}>
                  <AppDateInput
                    value={date}
                    onChange={onDateChange}
                    inputClassName="h-10 text-xs font-medium"
                  />
                </div>
              </>
            )}

            {showTillDate && (
              <>
                <span className="col-span-12 sm:col-span-1 text-left sm:text-center text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  {tillDateLabel}
                </span>
                <div className="col-span-12 sm:col-span-4">
                  <AppDateInput
                    value={tillDate}
                    onChange={onTillDateChange}
                    inputClassName="h-10 text-xs font-medium"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Plan Time & Till Time */}
        {showTime && (
          <div className="grid grid-cols-12 gap-4 items-center">
            <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
              Plan Time<span className="text-error font-bold"> *</span>
            </span>
            <div className="col-span-12 sm:col-span-4">
              {planTime !== undefined ? (
                <AppEditableTimeField
                  value={planTime}
                  onChange={onPlanTimeChange}
                />
              ) : (
                <AppEditableTimeField
                  hour={planHour}
                  minute={planMinute}
                  ampm={planAmPm}
                  onHourChange={onPlanHourChange}
                  onMinuteChange={onPlanMinuteChange}
                  onAmPmChange={onPlanAmPmChange}
                />
              )}
            </div>

            <span className="col-span-12 sm:col-span-1 text-left sm:text-center text-xs font-black uppercase tracking-wider text-on-surface-variant">
              Till Time<span className="text-error font-bold"> *</span>
            </span>
            <div className="col-span-12 sm:col-span-4">
              {tillTime !== undefined ? (
                <AppEditableTimeField
                  value={tillTime}
                  onChange={onTillTimeChange}
                />
              ) : (
                <AppEditableTimeField
                  hour={tillHour}
                  minute={tillMinute}
                  ampm={tillAmPm}
                  onHourChange={onTillHourChange}
                  onMinuteChange={onTillMinuteChange}
                  onAmPmChange={onTillAmPmChange}
                />
              )}
            </div>
          </div>
        )}

      </div>
    </section>
  )
}
